import { AxiosHttpClientAdapter } from '@/core/services/network/infra/adapters/axios.adapter';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type { PostOrderAddressRepository } from '../../domain/interfaces/PostOrderAddressRepository';
import type { PendingAddressData, AddressSubmission, PincodeDetails } from '../../domain/models/postOrderAddress.types';
import type { ApiPendingAddressResponse, SubmitAddressResponse, FormBody } from '../dto/postOrderAddress.dto';
import { adaptPendingAddressToDomain, flattenAddressForApi } from '../mappers/postOrderAddress.mapper';

const GET_PENDING_ADDRESS_ENDPOINT = '/order/address/pending/react';
const GET_SECURE_PENDING_ADDRESS_ENDPOINT = '/order/address/secure/pending/react';

const SUBMIT_ADDRESS_ENDPOINT = '/order/address/attach';
const SUBMIT_SECURE_ADDRESS_ENDPOINT = '/order/address/secure/attach';

const GET_PINCODE_DETAILS_ENDPOINT = '/address/';
const FORM_HEADERS = { 'Content-Type': 'application/x-www-form-urlencoded' } as const;

export class PostOrderAddressAdapter implements PostOrderAddressRepository {
  private httpClient = new AxiosHttpClientAdapter({
    baseURL: getApiBaseUrl(),
  });

  private async postForm<T>(endpoint: string, body: FormBody): Promise<T> {
    const form = new URLSearchParams();
    for (const [key, value] of Object.entries(body)) {
      if (value !== undefined && value !== null && value !== '') {
        form.append(key, String(value));
      }
    }
    return this.httpClient.post<T, URLSearchParams>(endpoint, form, { headers: { ...FORM_HEADERS } });
  }

  async getPendingAddress(params: { orderCode?: string | null; orderToken?: string | null }): Promise<PendingAddressData> {
    const isSecure = !!params.orderToken;
    const endpoint = isSecure ? GET_SECURE_PENDING_ADDRESS_ENDPOINT : GET_PENDING_ADDRESS_ENDPOINT;
    const queryStr = isSecure 
      ? `orderToken=${encodeURIComponent(params.orderToken!)}` 
      : `orderCode=${encodeURIComponent(params.orderCode!)}`;

    const data = await this.httpClient.get<ApiPendingAddressResponse>(`${endpoint}?${queryStr}`);
    
    const mappedData = adaptPendingAddressToDomain(data);
    mappedData.orderCode = mappedData.orderCode || params.orderCode || undefined;
    mappedData.orderToken = mappedData.orderToken || params.orderToken || undefined;
    
    return mappedData;
  }

  async getPincodeDetails(pincode: string): Promise<PincodeDetails> {
    return this.httpClient.get<PincodeDetails>(
      `${GET_PINCODE_DETAILS_ENDPOINT}?ajax=true&submiPtincode=true&pincode=${pincode}`
    );
  }

  async submitAddress(submission: AddressSubmission): Promise<void> {
    const isSecure = !!submission.orderToken;
    const endpoint = isSecure ? SUBMIT_SECURE_ADDRESS_ENDPOINT : SUBMIT_ADDRESS_ENDPOINT;
    
    const payload = flattenAddressForApi(submission);
    const response = await this.postForm<SubmitAddressResponse>(endpoint, payload);

    if (response && response.hasError) {
      throw new Error(response.message || "An error occurred while saving the address.");
    }
  }
}